import type { FieldSelection, JavascriptSerializable, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CollectionCodeFieldObject, CollectionCodeSelection, CollectionCodeSnippetOptions } from './types';
import { getCollectionFields, toJavascriptSerializable } from '#layers/data-model/public';

/**
 * Returns the default scalar field selection used when the caller has not selected fields.
 */
export function getDefaultCollectionCodeFields(collection: ResolvedMonospaceCollection): string[] {
	return getCollectionFields(collection, {
		includeRelationFields: false,
		includeUnsupportedFields: false,
	}).map((field) => field.apiName);
}

/**
 * Resolves explicit field input or falls back to the collection default scalar fields.
 */
export function resolveCodeSelection(options: CollectionCodeSnippetOptions): FieldSelection {
	return normalizeCodeSelection(options.selection) ?? { fields: getDefaultCollectionCodeFields(options.collection) };
}

/**
 * Normalizes UI field objects and field selections to a FieldSelection.
 */
export function normalizeCodeSelection(input: CollectionCodeSelection | undefined): FieldSelection | undefined {
	if (!input) return;
	if (!Array.isArray(input)) return input;
	if (input.length === 0) return;
	const selection: FieldSelection = { fields: [] };

	for (const field of input) {
		if (isCollectionCodeFieldObject(field) && 'oppositeCollectionId' in field) {
			selection.include ??= {};
			selection.include[field.apiName] = { fields: ['*'] };
		}
		else {
			selection.fields!.push(isCollectionCodeFieldObject(field) ? field.apiName : field);
		}
	}

	return selection;
}

/**
 * Converts a field selection into values supported by the snippet serializer.
 */
export function toSerializableFieldSelection(selection: FieldSelection): { [key: string]: JavascriptSerializable } {
	return Object.fromEntries(Object.entries(selection).map(([key, value]) => [key, toJavascriptSerializable(value)]));
}

/**
 * Checks whether a field selection entry is a resolved collection field object.
 */
function isCollectionCodeFieldObject(value: unknown): value is CollectionCodeFieldObject {
	return typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
		&& 'apiName' in value
		&& typeof value.apiName === 'string';
}
